import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { ActivityIndicator } from 'components/BlockingLoader/ActivityIndicator';
import { Button, ButtonTheme } from 'components/Button';
import { ButtonGroup } from 'components/ButtonGroup';
import { NavigationStacks, Screens, Stacks } from 'components/Navigation/types';
import { ListRenderItem } from '@shopify/flash-list';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { useDispatch } from 'react-redux';
import { ICONS } from 'components/Icon';
import { List } from 'components/List';
import { Page } from 'components/Page';
import { PageEmpty } from 'components/PageEmpty';
import { Pill } from 'components/Pill';
import { Txt } from 'components/Txt';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { ScreenSize } from 'lib/constants';
import {
  doTasksAssignedDetailsLoad,
  doTasksAssignedFilterOpenModal,
  doTasksAssignedFilterOptionsLoad,
  doTasksAssignedLoad,
} from 'store/tasks/actions';
import { rbAddReferencePrefix } from 'lib/riskbase';
import { theme } from 'lib/theme';

import {
  selectTasksAssignedFilterOptionsLoadStatus,
  selectTasksAssignedDueGroups,
  selectTasksAssignedError,
  selectTasksAssignedIsLoaded,
  selectTasksAssignedIsLoading,
  selectTasksAssignedItems,
  selectTasksAssignedIsTaskSelected,
} from 'store/tasks/selectors';
import { TaskDueGroup, TaskListItem } from 'types/Tasks';
import { TaskDetailsPanel } from './TasksAssignedDetailScreen';
import { TasksAssignedHeader } from './TasksAssignedHeader';
import { TASK_SEARCH_MAX_LENGTH, TaskStatusType } from '@riskbase/types';
import {
  getDueDateIconColor,
  getDueDateLabel,
  getStatusIconColor,
} from './utilities';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { CardRow } from 'components/CardRow';
import { Card } from 'components/Card';

type ListProps = NativeStackScreenProps<
  NavigationStacks[Stacks.TasksAssigned],
  Screens.TasksAssignedList
>;

type TaskGroup = {
  dueGroup: TaskDueGroup;
  tasks: TaskListItem[];
};

type TaskListRow =
  | {
      type: 'header';
      group: TaskGroup;
    }
  | {
      type: 'task';
      task: TaskListItem;
    };

function getTaskGroups(
  tasks: TaskListItem[],
  dueGroups: TaskDueGroup[],
): TaskGroup[] {
  const groupsById = new Map<string, TaskGroup>(
    dueGroups.map((dueGroup) => [
      dueGroup.id,
      {
        dueGroup,
        tasks: [],
      },
    ]),
  );

  tasks.forEach((task) => {
    groupsById.get(task.dueGroupId)?.tasks.push(task);
  });

  return Array.from(groupsById.values()).filter(
    (group) => group.tasks.length > 0,
  );
}

function getTaskListRows(groups: TaskGroup[]): TaskListRow[] {
  return groups.flatMap((group) => [
    {
      type: 'header' as const,
      group,
    },
    ...group.tasks.map((task) => ({
      type: 'task' as const,
      task,
    })),
  ]);
}

const TaskGroupHeader: React.FC<{ group: TaskGroup }> = ({ group }) => {
  return (
    <View style={styles.groupHeader}>
      <Txt size="small" weight="medium" style={styles.groupTitle}>
        {group.dueGroup.name}
      </Txt>
      <Txt size="xsmall" style={styles.groupCount}>
        {group.tasks.length}
      </Txt>
    </View>
  );
};

const TaskRow: React.FC<{
  task: TaskListItem;
  onPress: () => void;
}> = ({ task, onPress }) => {
  const isSelected = useSelector((state) =>
    selectTasksAssignedIsTaskSelected(state, task.id),
  );
  const isComplete = task.statusType === TaskStatusType.completed;
  return (
    <Card>
      <CardRow
        accessibilityLabel={`Open task ${task.title}`}
        accessibilityHint="Shows task details"
        onPress={onPress}
        selected={isSelected}
        style={isComplete && styles.taskRowComplete}
      >
        <View style={styles.taskContent}>
          <Txt
            size="small"
            weight="regular"
            numberOfLines={2}
            style={[styles.taskTitle, isComplete && styles.taskTitleComplete]}
          >
            {task.title || task.description}
          </Txt>
          <SeparatorHorizontal />
          <View style={styles.chips}>
            <Txt
              size="xxxsmall"
              weight="bold"
              numberOfLines={1}
              style={{ color: theme.colors.fieldValueText }}
            >
              {rbAddReferencePrefix(task.reference)}
            </Txt>
            <Pill
              withIcon={true}
              iconSize={8}
              iconColor={getStatusIconColor(task.statusType)}
              label={task.statusName}
              textSize="xxxsmall"
              textWeight="bold"
            />
            <Pill
              withIcon={true}
              iconSize={8}
              iconColor={getDueDateIconColor(task.dueDate)}
              label={getDueDateLabel(task.dueDate)}
              textSize="xxxsmall"
              textWeight="bold"
            />
          </View>
        </View>
      </CardRow>
      {/* </Pressable> */}
    </Card>
  );
};

export const TasksAssignedListScreen: React.FC<ListProps> = ({
  navigation,
}) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize(true);
  const tasks = useSelector(selectTasksAssignedItems);
  const dueGroups = useSelector(selectTasksAssignedDueGroups);
  const isLoading = useSelector(selectTasksAssignedIsLoading);
  const isLoaded = useSelector(selectTasksAssignedIsLoaded);
  const error = useSelector(selectTasksAssignedError);
  const filterOptionsLoadStatus = useSelector(
    selectTasksAssignedFilterOptionsLoadStatus,
  );
  const isLargeScreen = screenSize === ScreenSize.large;

  const handleSearchChange = React.useCallback(
    (value: string) => {
      dispatch(
        doTasksAssignedLoad({
          filter: {
            search: value.trim().slice(0, TASK_SEARCH_MAX_LENGTH),
          },
        }),
      );
    },
    [dispatch],
  );

  React.useEffect(() => {
    dispatch(doTasksAssignedFilterOptionsLoad({ loadTasksOnSuccess: true }));
  }, [dispatch]);

  const taskGroups = React.useMemo(
    () => getTaskGroups(tasks, dueGroups),
    [dueGroups, tasks],
  );
  const taskListRows = React.useMemo(
    () => getTaskListRows(taskGroups),
    [taskGroups],
  );

  const handleTaskPress = React.useCallback(
    (taskId: string) => {
      dispatch(doTasksAssignedDetailsLoad({ taskId }));
      if (!isLargeScreen) {
        navigation.navigate(Screens.TasksAssignedDetail);
      }
    },
    [dispatch, isLargeScreen, navigation],
  );

  const renderItem: ListRenderItem<TaskListRow> = React.useCallback(
    ({ item }) => {
      if (item.type === 'header') {
        return <TaskGroupHeader group={item.group} />;
      }
      return (
        <TaskRow
          task={item.task}
          onPress={() => handleTaskPress(item.task.id)}
        />
      );
    },
    [handleTaskPress],
  );

  return (
    <View style={styles.container}>
      <Page
        backgroundColor={theme.colors.background}
        header={<TasksAssignedHeader onSearch={handleSearchChange} />}
      >
        <View style={[styles.body, isLargeScreen && styles.bodySplit]}>
          <View style={styles.listPane}>
            {(isLoading ||
              filterOptionsLoadStatus === null ||
              filterOptionsLoadStatus === 'loading') &&
            !isLoaded ? (
              <View style={styles.loader}>
                <ActivityIndicator size={56} />
              </View>
            ) : error ? (
              <View style={styles.messageCard}>
                <Txt weight="bold" style={styles.errorTitle}>
                  Unable to load tasks
                </Txt>
                <Txt style={styles.errorText}>{error}</Txt>
              </View>
            ) : !isLoaded ? (
              <PageEmpty icon={ICONS.RBPlectrumLight}>
                <ButtonGroup>
                  <Button
                    text="Load"
                    icon={ICONS.RotateRightLight}
                    buttonTheme={ButtonTheme.themeless}
                    onPress={() => dispatch(doTasksAssignedLoad({}))}
                  />
                  <Button
                    text="Filter"
                    icon={ICONS.SlidersUpLight}
                    buttonTheme={ButtonTheme.themeless}
                    onPress={() => dispatch(doTasksAssignedFilterOpenModal())}
                  />
                </ButtonGroup>
              </PageEmpty>
            ) : isLoaded && !tasks.length ? (
              <PageEmpty
                icon={ICONS.RBPlectrumLight}
                text="No Tasks Found"
                subtext="Try changing your filter"
              />
            ) : (
              <List
                data={taskListRows}
                keyExtractor={(item) =>
                  item.type === 'header'
                    ? `header-${item.group.dueGroup.id}`
                    : `task-${item.task.id}`
                }
                renderItem={renderItem}
                getItemType={(item) => item.type}
                paddingTop={0}
                paddingBottom={theme.padding * 2}
                ItemSeparatorComponent={() => (
                  <View style={{ height: theme.padding / 2 }} />
                )}
              />
            )}
          </View>
          {isLargeScreen && (
            <View style={styles.detailsPane}>
              <TaskDetailsPanel isMobile={false} />
            </View>
          )}
        </View>
      </Page>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative',
  },
  body: {
    flex: 1,
  },
  bodySplit: {
    flexDirection: 'row',
  },
  listPane: {
    flex: 1,
  },
  loader: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
  messageCard: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.cardBorderRadius,
    margin: theme.padding,
    padding: theme.padding * 2,
  },
  errorTitle: {
    color: theme.colors.red,
    marginBottom: theme.paddingHorizontalHalf,
  },
  errorText: {
    color: theme.colors.fieldValueText,
  },
  groupHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.padding / 2,
    paddingLeft: theme.padding * 2,
    paddingRight: theme.padding * 2,
    paddingTop: theme.padding,
    paddingBottom: 0,
  },
  groupTitle: {
    color: theme.colors.text,
  },
  groupCount: {
    color: theme.colors.fieldValueText,
  },
  taskRowComplete: {
    opacity: 0.68,
  },
  taskContent: {
    flex: 1,
    justifyContent: 'space-between',
    gap: theme.padding / 2,
  },
  taskTitle: {
    color: theme.colors.text,
  },
  taskTitleComplete: {
    textDecorationLine: 'line-through',
  },
  location: {
    color: theme.colors.fieldValueText,
  },
  chips: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 2,
  },
  detailsPane: {
    borderLeftColor: theme.colors.black10,
    borderLeftWidth: 1,
    flex: 1,
  },
});
